import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Globe, MapPin, Thermometer, Luggage, Bus, Home, Sparkles,
  MessageCircle, Snowflake, Sun, Leaf, Flower2, Train, ArrowRight,
  Wallet, Briefcase, UtensilsCrossed, ShieldCheck, HelpCircle,
} from "lucide-react";
import { Footer } from "@/components/site/Footer";
import { useLang, LANGS } from "@/context/LangContext";
import POLAND from "@/data/polandGuide";

const IMG = {
  hero: "https://images.unsplash.com/photo-1648056862293-d93b3b037bfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  krakow: "https://images.unsplash.com/photo-1642278167684-4d8a6bd75def?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  wroclaw: "https://images.unsplash.com/photo-1753871486046-5f7c00100138?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  river: "https://images.unsplash.com/photo-1739960069170-0a0206957122?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  tram: "https://images.unsplash.com/photo-1768752555540-44be6fb53c12?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  winter: "https://images.unsplash.com/photo-1669326220646-1a5a67d11b92?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
};

const SEASON_ICONS = [Flower2, Sun, Leaf, Snowflake];

export default function PolandGuide() {
  const { lang, changeLang, t } = useLang();
  const g = POLAND[lang] || POLAND.es;
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className="bg-[#0B132B] min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0B132B]/95 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="poland-logo" className="flex items-center gap-2 text-white">
            <ArrowLeft size={18} /> <span className="text-xl font-head font-extrabold">europe<span className="text-[#FFD166]">jobs</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1 border border-white/15 rounded-full p-1">
              {LANGS.map((l) => (
                <button key={l.code} data-testid={`poland-lang-${l.code}`} onClick={() => changeLang(l.code)}
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${lang === l.code ? "bg-[#FFD166] text-[#0B132B]" : "text-slate-200 hover:text-[#FFD166]"}`}>
                  {l.flag} {l.label}
                </button>
              ))}
            </div>
            <Link to="/#aplicar" data-testid="poland-apply" className="bg-[#FFD166] text-[#0B132B] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#E0A926] transition-colors">
              {g.ctaBtn}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section data-testid="poland-hero" className="relative pt-28 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMG.hero} alt="Varsovia, Polonia" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/85 to-[#0B132B]/60" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-[#FFD166]/15 text-[#FFD166] border border-[#FFD166]/40 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
            <MapPin size={14} /> {g.hero.badge}
          </span>
          <h1 className="font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 text-white leading-tight">{g.hero.title} 🇵🇱</h1>
          <p className="text-slate-300 text-base md:text-lg mt-5 max-w-2xl mx-auto">{g.hero.subtitle}</p>
        </div>
      </section>

      {/* Facts */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.factsTitle}</h2>
          <div className="ej-accent-bar mt-4" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
            {g.facts.map((f, i) => (
              <div key={i} className="bg-[#F5F7FB] border border-slate-200 rounded-2xl p-5 card-hover">
                <div className="text-xs text-slate-500 uppercase tracking-wide">{f.k}</div>
                <div className="text-lg font-head font-bold text-[#0B132B] mt-1">{f.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Climate */}
      <section className="py-14 md:py-20 bg-[#F5F7FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Thermometer className="text-[#E63946]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.climateTitle}</h2>
          </div>
          <p className="text-slate-600 mt-3 max-w-2xl">{g.climateIntro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {g.seasons.map((s, i) => {
              const Icon = SEASON_ICONS[i] || Sun;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 card-hover">
                  <Icon className="text-[#FFD166]" style={{ color: ["#22C55E", "#F59E0B", "#EA580C", "#38BDF8"][i] }} />
                  <div className="font-head font-bold text-[#0B132B] mt-3">{s.name}</div>
                  <div className="text-sm font-semibold text-[#E63946] mt-1">{s.temp}</div>
                  <div className="text-sm text-slate-600 mt-2">{s.desc}</div>
                </div>
              );
            })}
          </div>
          <img src={IMG.winter} alt="Invierno en Polonia" className="w-full h-64 object-cover rounded-2xl mt-6 border border-slate-200" />
        </div>
      </section>

      {/* What to bring */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Luggage className="text-[#0B132B]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.bringTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {g.bring.map((grp, i) => (
              <div key={i} className="bg-[#F5F7FB] border border-slate-200 rounded-2xl p-6">
                <div className="font-head font-bold text-lg text-[#0B132B]">{grp.title}</div>
                <ul className="mt-3 space-y-2">
                  {grp.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFD166] shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transport */}
      <section className="py-14 md:py-20 bg-[#0B132B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-3">
              <Bus className="text-[#FFD166]" />
              <h2 className="font-head font-extrabold text-2xl md:text-4xl text-white">{g.transportTitle}</h2>
            </div>
            <p className="text-slate-300 mt-3">{g.transportIntro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              {g.transport.map((tr, i) => (
                <div key={i} className="bg-[#1C2541] border border-white/10 rounded-xl p-4 card-hover">
                  <div className="flex items-center gap-2 text-white font-semibold text-sm"><Train size={15} className="text-[#FFD166]" /> {tr.name}</div>
                  <div className="text-xs text-slate-400 mt-1">{tr.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <img src={IMG.tram} alt="Transporte en Polonia" className="rounded-2xl border border-white/10 h-80 w-full object-cover" />
        </div>
      </section>

      {/* Life + photos */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Home className="text-[#0B132B]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.lifeTitle}</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6 items-center">
            <div className="space-y-4">
              {g.lifeText.map((p, i) => <p key={i} className="text-slate-600">{p}</p>)}
            </div>
            <img src={IMG.wroclaw} alt="Wrocław, Polonia" className="rounded-2xl border border-slate-200 h-72 w-full object-cover" />
          </div>
          <div className="mt-8">
            <div className="text-sm font-semibold text-[#E63946] uppercase tracking-wider">{g.photosTitle}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <img src={IMG.krakow} alt="Cracovia" className="rounded-2xl border border-slate-200 h-56 w-full object-cover card-hover" />
              <img src={IMG.river} alt="Río Vístula" className="rounded-2xl border border-slate-200 h-56 w-full object-cover card-hover" />
              <img src={IMG.hero} alt="Varsovia" className="rounded-2xl border border-slate-200 h-56 w-full object-cover card-hover" />
            </div>
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section className="py-14 md:py-20 bg-[#F5F7FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Sparkles className="text-[#FFD166]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.oppTitle}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {g.opp.map((o, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 card-hover">
                <div className="w-10 h-10 rounded-xl bg-[#FFD166]/15 flex items-center justify-center text-[#B8860B] font-head font-extrabold">{i + 1}</div>
                <div className="font-head font-bold text-[#0B132B] mt-4">{o.title}</div>
                <div className="text-sm text-slate-600 mt-1">{o.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Money */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Wallet className="text-[#16a34a]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.moneyTitle}</h2>
          </div>
          <p className="text-slate-600 mt-3 max-w-2xl">{g.moneyIntro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {g.money.map((m, i) => (
              <div key={i} className="bg-[#F5F7FB] border border-slate-200 rounded-2xl p-5 card-hover">
                <div className="text-xs text-slate-500 uppercase tracking-wide">{m.k}</div>
                <div className="text-xl font-head font-extrabold text-[#16a34a] mt-1">{m.v}</div>
                <div className="text-sm text-slate-600 mt-1">{m.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work day-to-day */}
      <section className="py-14 md:py-20 bg-[#0B132B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Briefcase className="text-[#FFD166]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-white">{g.workTitle}</h2>
          </div>
          <p className="text-slate-300 mt-3 max-w-2xl">{g.workIntro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {g.work.map((w, i) => (
              <div key={i} className="bg-[#1C2541] border border-white/10 rounded-2xl p-5 card-hover">
                <div className="font-head font-bold text-white">{w.title}</div>
                <div className="text-sm text-slate-400 mt-1">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Food */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="text-[#E63946]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.foodTitle}</h2>
          </div>
          <p className="text-slate-600 mt-3 max-w-2xl">{g.foodIntro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {g.food.map((fd, i) => (
              <div key={i} className="bg-[#F5F7FB] border border-slate-200 rounded-2xl p-5 card-hover">
                <div className="font-head font-bold text-[#0B132B]">{fd.name}</div>
                <div className="text-sm text-slate-600 mt-1">{fd.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & health */}
      <section className="py-14 md:py-20 bg-[#F5F7FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-[#16a34a]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.safetyTitle}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            {g.safety.map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 card-hover flex gap-4">
                <ShieldCheck size={22} className="text-[#16a34a] shrink-0 mt-0.5" />
                <div>
                  <div className="font-head font-bold text-[#0B132B]">{s.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Adaptation + phrases */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.adaptTitle}</h2>
            <div className="ej-accent-bar mt-4" />
            <div className="space-y-4 mt-6">
              {g.adaptTips.map((a, i) => (
                <div key={i} className="flex gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#0B132B] text-[#FFD166] font-bold flex items-center justify-center shrink-0 text-sm">{i + 1}</span>
                  <div>
                    <div className="font-semibold text-[#0B132B]">{a.title}</div>
                    <div className="text-sm text-slate-600">{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#0B132B] rounded-3xl border border-white/10 p-6 md:p-8">
            <div className="flex items-center gap-2 text-[#FFD166] font-head font-bold text-lg"><MessageCircle size={18} /> {g.phrasesTitle}</div>
            <div className="mt-4 divide-y divide-white/10">
              {g.phrases.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-3">
                  <span className="font-head font-bold text-white">{p.pl}</span>
                  <span className="text-sm text-slate-400">{p.m}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-[#F5F7FB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <HelpCircle className="text-[#0B132B]" />
            <h2 className="font-head font-extrabold text-2xl md:text-4xl text-[#0B132B]">{g.faqTitle}</h2>
          </div>
          <div className="mt-8 space-y-3">
            {g.faq.map((q, i) => (
              <details key={i} data-testid={`poland-faq-${i}`} className="group bg-white border border-slate-200 rounded-2xl p-5">
                <summary className="flex items-center justify-between gap-4 font-head font-bold text-[#0B132B] list-none cursor-pointer">
                  {q.q}
                  <span className="text-[#FFD166] group-open:rotate-45 transition-transform text-2xl leading-none shrink-0">+</span>
                </summary>
                <p className="text-sm text-slate-600 mt-3">{q.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#0d1730]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-head font-extrabold text-3xl md:text-4xl text-white">{g.ctaTitle}</motion.h2>
          <p className="text-slate-300 mt-3">{g.ctaDesc}</p>
          <Link to="/#aplicar" data-testid="poland-cta-apply"
            className="inline-flex items-center gap-2 mt-7 bg-[#FFD166] text-[#0B132B] font-bold px-8 py-4 rounded-full hover:bg-[#E0A926] transition-colors">
            {g.ctaBtn} <ArrowRight size={18} />
          </Link>
          <div className="mt-6">
            <Link to="/" className="text-slate-400 hover:text-white text-sm inline-flex items-center gap-1.5"><ArrowLeft size={14} /> {g.back}</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
